import {
  ColdSubscription,
  listen,
  multitouch,
  pointer,
  tween,
  value,
  ValueReaction,
} from "popmotion";
import { HotSubscription } from "popmotion/lib/reactions/types";

interface ZoomParameters {
  sensitivity?: number;
  min?: number;
  max?: number;
  initialValue?: string;
}

type DragZoomOptions = {
  zoom: ZoomParameters;
};

type TranslateParameters = {
  x: number;
  y: number;
};
const defaultZoomParameters: ZoomParameters = {
  sensitivity: 1 / 500,
  min: 1,
  max: 4,
  initialValue: "contain",
};

function valInRange(val: number, min: number, max: number) {
  return Math.min(Math.max(min, val), max);
}

function getMeanXY(evt: any) {
  const t1 = evt.touches[0];
  const t2 = evt.touches[1];
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  };
}

class DragZoom {
  //Parameters
  zoomParameters: ZoomParameters;
  //Subscriptions
  private zoomSub: HotSubscription;
  private subPointerUp: ColdSubscription;
  private subPinchStart: ColdSubscription;
  private subPinchTrack: ColdSubscription;
  private subMouseDragStart: ColdSubscription;
  private subMouseDrag: ColdSubscription;
  private subWheel: ColdSubscription;
  //Elements
  private element: HTMLElement;
  readonly wrapper: HTMLElement;
  //Etat
  private mouseDragActive: boolean;
  private zoomOnWheel: boolean;
  private translate: TranslateParameters;
  private zoom: number;
  private lastTranslate: TranslateParameters;
  private dragBoundaries: {
    xmin: number;
    xmax: number;
    ymin: number;
    ymax: number;
  };
  private baseScale: number;
  private minZoom: number;
  private size: {
    width: number;
    height: number;
  };
  private elementSize: {
    width: number;
    height: number;
  };
  private renderFrameRequest: any;
  private coverZoom: number;
  public onChange: () => void;

  constructor(elt: HTMLElement, options?: DragZoomOptions) {
    this.zoomParameters = { ...defaultZoomParameters, ...options?.zoom };
    this.element = elt;
    this.wrapper = document.createElement("div");
    this.wrapper.style.overflow = "hidden";
    this.wrapper.appendChild(elt);
    this.zoom = 1;
    this.translate = {
      x: 0,
      y: 0,
    };
    this.lastTranslate = {
      x: 0,
      y: 0,
    };
    this.zoomOnWheel = true;
    this.initListeners();
  }

  incZoom(val: number) {
    const currentZoom = this.zoom;
    let zoom = currentZoom + val * this.zoomParameters.sensitivity;
    tween({ from: currentZoom, to: zoom, duration: 500 }).start((val: any) => {
      this.setZoom(val);
    });
    return zoom;
  }

  setZoomParameters(params: ZoomParameters) {
    Object.assign(this.zoomParameters, params);
    this.render();
  }

  setZoom(val: number) {
    this.zoom = valInRange(
      val,
      this.zoomParameters.min,
      this.zoomParameters.max
    );
    this.render();
  }

  setTranslation(t: { x: number; y: number }) {
    this.translate = t;
    this.render();
  }

  onChangeHandler() {
    const evt = this.getCurrentState();
    this.onChange?.();
  }

  getCurrentState() {
    return {
      translate: this.translate,
      size: this.size,
      elementSize: this.elementSize,
      baseScale: this.baseScale,
      zoom: this.zoom,
    };
  }

  initZoomListeners() {
    this.subPinchStart = listen(this.wrapper, "touchstart")
      .filter(({ touches }) => touches?.length >= 2)
      .start((evt: any) => {
        evt.preventDefault();
        evt.stopPropagation();
        //const start = getMeanXY(evt);
        this.subPinchTrack = multitouch({
          scale: this.zoom,
        }).start((evt: any) => {
          this.setZoom(evt.scale);
          if (evt.touches.length !== 2) {
            this.subPinchTrack.stop();
            return;
          }
        });
      });
    this.subWheel = listen(this.wrapper, "wheel", true)
      .pipe((event: any) => {
        const { wheelDelta } = event;
        const val = this.zoom;
        if (this.zoomOnWheel) {
          event.preventDefault();
          event.stopPropagation();
          return val * (1 + (wheelDelta * this.zoomParameters.sensitivity) / 4);
        } else {
          return val;
        }
      })
      .start((val: any) => {
        this.setZoom(val);
      });
    this.subPointerUp = listen(document, "touchend").start(() => {
      this.subPinchTrack?.stop();
    });
    this.wrapper.addEventListener("wheel", (evt) => {
      if (this.mouseDragActive) {
        evt.preventDefault();
        return false;
      } else return true;
    });
  }

  render() {
    if (this.renderFrameRequest) return;
    this.renderFrameRequest = requestAnimationFrame(() => {
      if (!this.size) return;

      const { x, y } = this.translate;
      const zoom = this.zoom;
      const scale = zoom * this.baseScale;
      const { width, height } = this.size;
      const { width: elWidth, height: elHeight } = this.elementSize;
      const xoffset = Math.max(elWidth * scale - width, 0) / 2;
      const yoffset = Math.max(elHeight * scale - height, 0) / 2;
      this.dragBoundaries = {
        xmin: -1 * xoffset,
        xmax: xoffset,
        ymin: -1 * yoffset,
        ymax: yoffset,
      };
      this.translate = {
        x: valInRange(x, this.dragBoundaries.xmin, this.dragBoundaries.xmax),
        y: valInRange(y, this.dragBoundaries.ymin, this.dragBoundaries.ymax),
      };
      const transformString = `translate( ${this.translate.x}px , ${this.translate.y}px ) scale(${scale})`;
      this.element.style.transform = transformString;
      this.renderFrameRequest = null;
      this.onChange();
    });
  }

  initDragListeners() {
    this.subMouseDragStart = listen(this.wrapper, "mousedown touchstart")
      .filter(() => {
        return this.mouseDragActive;
      })
      .start((evt: MouseEvent) => {
        evt.stopPropagation();
        this.subMouseDrag = pointer({ x: 0, y: 0 }).start(
          ({ x, y }: { x: number; y: number }) => {
            const { x: lastX, y: lastY } = this.lastTranslate;
            this.setTranslation({
              x: lastX + x,
              y: lastY + y,
            });
          }
        );
      });
    listen(document, "mouseup touchend").start(() => {
      if (this.subMouseDrag) this.subMouseDrag.stop();
      this.lastTranslate = this.translate;
    });
  }

  resize({ width, height }: { width: number; height: number }) {
    const wrapper = this.wrapper;
    wrapper.style.width = `${width}px`;
    wrapper.style.height = `${height}px`;

    const prevSize = this.size;
    this.size = { width, height };

    this.initScale();
    if (prevSize) {
      const { width: oldWidth, height: oldHeight } = prevSize;
      const newTranslation = {
        x: (this.translate.x / oldWidth) * width,
        y: (this.translate.y / oldHeight) * height,
      };
      this.setTranslation(newTranslation);
      this.lastTranslate = newTranslation;
    } else {
      if (this.zoomParameters.initialValue === "cover") {
        this.setZoom(this.coverZoom);
      }
    }
  }

  initScale() {
    if (!this.elementSize) {
      const { width: eltWidth, height: eltHeight } =
        this.element.getBoundingClientRect();
      this.elementSize = {
        width: eltWidth,
        height: eltHeight,
      };
    }
    const { width, height } = this.elementSize;
    const { width: wrapperWidth, height: wrapperHeight } = this.size;
    const scale1 = wrapperWidth / width;
    const scale2 = wrapperHeight / height;
    this.baseScale = Math.min(scale1, scale2);
    this.coverZoom = Math.max(scale1, scale2) / Math.min(scale1, scale2);
    this.element.style.marginTop = `${(wrapperHeight - height) / 2}px`;
    this.element.style.marginLeft = `${(wrapperWidth - width) / 2}px`;
    this.render();
  }

  initListeners() {
    this.initZoomListeners();
    this.initDragListeners();
  }

  setMouseDragMode(active: boolean) {
    this.mouseDragActive = active;
  }

  setZoomOnWheel(active: boolean) {
    this.zoomOnWheel = active;
  }
}

export { DragZoom, DragZoomOptions };
